import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import * as d3TimeFormat from 'd3-time-format';
import { SettlementsService } from 'src/app/services/settlements.service';
import { DateService } from 'src/app/shared/services/date.service';

@Component({
  selector: 'app-index-chart',
  template: `<figure id="line" #line></figure>`,
  styles: [`
    .dashed-line {
      stroke-dasharray: 5, 5; /* Change these values to adjust the dash pattern */
    }
  `]
})
export class IndexChartComponent implements OnInit, OnChanges {
  @Input() public data: any;
  @Input() public tanque: any;
  @Input() public consumptions: any;
  @Input() public start_date: any;
  @Input() public end_date: any;
  @Input() public summary: any;
  @ViewChild('line', { static: true }) line: ElementRef<any> | undefined;

  
  
  private width = 1000;
  private height = 340;
  private margin = 44;

  public svg: any;
  public svgInner: any;
  public yScale: any;
  public xScale: any;
  public xAxis: any;
  public yAxis: any;
  public lineGroup: any;

  lineOpacity = "0.9";
  lineOpacityHover = "0.85";
  otherLinesOpacityHover = "0.1";
  lineStroke = "1.5px";
  lineStrokeHover = "2.5px";

  circleOpacity = '0.85';
  circleOpacityOnLineHover = "0.25"
  circleRadius = 6;
  circleRadiusHover = 8;
  duration = 250;

  readingItems: any[] = [];
  
  constructor(
    public chartElem: ElementRef,
    private dateService: DateService,
    private store: SettlementsService
  ) {}

  ngOnInit(): void {
    this.createSvg();
    
    // window.addEventListener('resize', () => this.drawChart(this.data));
    // this.drawChart(this.data)

    
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const shouldRedraw = 
      (changes['data'] && changes['data'].currentValue) ||
      (changes['consumptions'] && changes['consumptions'].currentValue);

    if (shouldRedraw) {
      if (this.line?.nativeElement.closest('.card')) {
        this.width = this.line.nativeElement.closest('.card').getBoundingClientRect().width - 60;
      }
      
      // Only draw if we have data
      if (this.data) {
        this.drawChart(this.data);
      }
    }

    // Handle window resize
    if (changes.hasOwnProperty('data') && this.data.length > 0) {
      const resizeHandler = () => {
        if (this.line?.nativeElement.closest('.card')) {
          this.width = this.line.nativeElement.closest('.card').getBoundingClientRect().width - 60;
        }
        this.drawChart(this.data);
      };

      window.removeEventListener('resize', resizeHandler); // Remove any existing handler
      window.addEventListener('resize', resizeHandler);
    }
  }

  private createSvg(): void {

    const dateValues: (Date)[] = this.data
      .map((row: any) => d3.timeParse("%Y-%m-%d")(row.date))
      .filter((date: Date): date is Date => date !== null);

    this.svg = d3
      .select('figure#line')
      .append('svg')
      .attr("width", (this.width+this.margin)+"px")
      .attr("height", (this.height+this.margin)+"px")
      .append('g')
      .attr("transform", `translate(${this.margin}, ${this.margin})`);
  }

  drawChart(data: any) {
    console.log("drawChart DATA ====> ", data);
    console.log('TANQUE DATA ===> ', this.tanque);
    if (data != undefined && data.length === 0) {
      return;
    }

    this.svg.selectAll("*").remove();    
    let startDate = new Date(`${this.start_date.year}-${this.start_date.month}-${this.start_date.day}`);
    let endDate = new Date(`${this.end_date.year}-${this.end_date.month}-${this.end_date.day}`);

    let processed_data = data.readings.map((d: any) => {
      return {
        id: d.id,
        date: new Date(d.created_at),
        valor: d.lts_estimated        
      }
    });

    // Group consumptions by month
    let monthlyConsumptions = this.consumptions?.reduce((acc: any, curr: any) => {
      const date = new Date(curr.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!acc[monthKey]) {
        acc[monthKey] = {
          date: new Date(date.getFullYear(), date.getMonth(), 1), // First day of month
          valor: 0
        };
      }
      
      acc[monthKey].valor += curr.liters;
      return acc;
    }, {});

    // Convert grouped data to array
    let processed_consumptions = monthlyConsumptions ? 
      Object.values(monthlyConsumptions) : [];

    // Find the maximum consumption value
    const maxConsumption = processed_consumptions.length > 0 
      ? Math.max(...processed_consumptions.map((d: any) => d.valor))
      : 0;

    // Set y-scale domain to maximum of tank capacity and maximum consumption
    const yDomainMax = Math.max(this.tanque.capacidad, maxConsumption);

    this.yScale = d3.scaleLinear()
      .domain([0, yDomainMax * 1.1]) // Add 10% padding to the top
      .range([this.height-this.margin, 0]);

    let processed_prediction = data.prediction.map((d: any) => {
      return {
        date: new Date(d.date),
        valor: Math.round(d.valor)
      }
    });

    let processed_children = data.children.map((child: any) => {
      return child.map((d: any) => {
        return {
          date: new Date(d.created_at),
          valor: Math.round(d.lts_estimated)
        }
      });
    });

    d3.timeFormatDefaultLocale({
      dateTime: "%A, %e de %B de %Y, %X",
      date: "%d/%m/%Y",
      time: "%H:%M:%S",
      periods: ["AM", "PM"] as [string, string],
      days: ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"],
      shortDays: ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"],
      months: [
        "enero", "febrero", "marzo", "abril", "mayo", "junio",
        "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
      ],
      shortMonths: [
        "ene", "feb", "mar", "abr", "may", "jun",
        "jul", "ago", "sep", "oct", "nov", "dic"
      ]
    });

    this.xScale = d3.scaleTime()      
      .domain([startDate, endDate])
      .range([0, this.width-this.margin]);


    // console.log('SAFE HABOR DATA ==> ', this.data.children);
    // this.yScale = d3.scaleLinear()
      // .domain([0, this.tanque.capacidad])
      // // .domain([0, d3.max([this.tanque.capacidad, d3.max(data.readings.map((d: any) => d.lts_estimated))])
      // // .domain([0, d3.max(processed_data.map((d: any) => d.lts_estimated) ])
      // .range([this.height-this.margin, 0]);

    // console.log('PROCCESD CHILDREN ===> ', processed_children);
    // console.log('PROCCESD DATA ===> ', processed_data);
    console.log('PROCCESD CONSUMPTIONS ===> ', processed_consumptions);
    // console.log('PROCCESD PREDICTION ===> ', processed_prediction);

    let ctl = this;
    let color = d3.scaleOrdinal(d3.schemeCategory10);

    

    
    // Convert percentages to liters
    const limiteInLiters = this.tanque.capacidad * (this.tanque.limite / 100);
    const limiteInferiorInLiters = this.tanque.capacidad * (this.tanque.limite_inferior / 100);

    // Calculate positions on the y-axis
    const limitePosition = this.yScale(limiteInLiters);
    const limiteInferiorPosition = this.yScale(limiteInferiorInLiters);

    // Draw vertical dashed lines when the year changes
    processed_data.forEach((d: any, i: number) => {
      if (i > 0) {
        const currentYear = d.date.getFullYear();
        const previousYear = processed_data[i - 1].date.getFullYear();

        if (currentYear !== previousYear) {
          const xPosition = this.xScale(d.date);

          this.svg.append("line")
            .attr("x1", xPosition)
            .attr("x2", xPosition)
            .attr("y1", 0)
            .attr("y2", this.height - this.margin)
            .attr("stroke", "gray")
            .attr("stroke-width", 1)
            .attr("stroke-dasharray", "5,5") // Dashed line
            .attr("class", "year-change-line");

          // Optionally, add a label for the year
          this.svg.append("text")
            .attr("x", xPosition + 5)
            .attr("y", 15)
            .attr("fill", "gray")
            .attr("font-size", "12px")
            .text(currentYear);
        }
      }
    });

    // Fill the space between the lines with light gray (append first to render behind other elements)
    this.svg.append("rect")
    .attr("x", 0)
    .attr("y", limitePosition)
    .attr("width", this.width - this.margin)
    .attr("height", limiteInferiorPosition - limitePosition)
    .attr("fill", "lightgray")
    .attr("opacity", 0.5);

    // Draw the green horizontal dashed line for limite
    this.svg.append("line")
    .attr("x1", 0)
    .attr("x2", this.width - this.margin)
    .attr("y1", limitePosition)
    .attr("y2", limitePosition)
    .attr("stroke", "green")
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "5,5") // Dashed line
    .attr("class", "limite-line");

    // Draw the red horizontal dashed line for limite_inferior
    this.svg.append("line")
    .attr("x1", 0)
    .attr("x2", this.width - this.margin)
    .attr("y1", limiteInferiorPosition)
    .attr("y2", limiteInferiorPosition)
    .attr("stroke", "red")
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "5,5") // Dashed line
    .attr("class", "limite-inferior-line");

    // Add labels for the lines
    this.svg.append("text")
    .attr("x", this.width - this.margin - 10)
    .attr("y", limitePosition - 5)
    .attr("text-anchor", "end")
    .attr("fill", "green")
    .text(`Límite (${limiteInLiters.toFixed(2)} L)`);

    this.svg.append("text")
    .attr("x", this.width - this.margin - 10)
    .attr("y", limiteInferiorPosition - 5)
    .attr("text-anchor", "end")
    .attr("fill", "red")
    .text(`Límite Inferior (${limiteInferiorInLiters.toFixed(2)} L)`);

    const lines = this.svg
      .append('g')      
      .attr('class', 'lines');
    
    const cricles = this.svg
      .append('g')
      .attr('class', 'circles')
      

    // Append the spline and other elements after the rectangle
    lines
    .datum(processed_prediction) // Red line prediction
    .append('path')
    .attr("d", d3.line()
      .curve(d3.curveCardinal) // Add this to make the line a spline
      .x(function(d: any) { return ctl.xScale(d.date) })
      .y(function(d: any) { return ctl.yScale(d.valor) })
    )
    .style('fill', 'none')
    .attr("stroke", "red")
    .attr("stroke-width", 2)
    .attr("class", "dashed-line")
    .style('opacity', this.lineOpacity);

    lines
      .datum(processed_prediction) // Red line prediction
      .append('path')
      .attr("d", d3.line()
        .curve(d3.curveCardinal) // Add this to make the line a spline
        .x(function(d: any) { return ctl.xScale(d.date) })
        .y(function(d: any) { return ctl.yScale(d.valor) })
      )
      .style('fill', 'none')
      .attr("stroke", "red")
      .attr("stroke-width", 2)
      .attr("class", "dashed-line")
      .style('opacity', this.lineOpacity);
    
    processed_children.forEach((child: any, i: any) => {
      lines
        .datum(child)
        .append('path')
        .attr("d", d3.line()
          .curve(d3.curveCardinal) // Add this to make the line a spline
          .x(function(d: any) { return ctl.xScale(d.date) })
          .y(function(d: any) { return ctl.yScale(d.valor) })
        )
        .style('fill', 'none')
        .attr("stroke", color(i))
        .attr("stroke-width", 2)
        .style('opacity', 0.6);
    });
    
    lines
      .datum(processed_data) // Main line data
      .append('path')
      .attr("d", d3.line()
        .curve(d3.curveCardinal) // Add this to make the line a spline
        .x(function(d: any) { return ctl.xScale(d.date) })
        .y(function(d: any) { return ctl.yScale(d.valor) })
      )
      .style('fill', 'none')
      .attr("stroke", "orange")
      .attr("stroke-width", 4)
      .style('opacity', "1");

    // Create the tooltip
    var tooltip = d3.select("figure#line")
      .append("div")
      .style("position", "absolute")
      .style("z-index", "10000")
      .style("visibility", "hidden")
      .style("background-color", "white")
      .style("border", "solid")
      .style("border-width", "1px")
      .style("border-radius", "5px")
      .style("padding", "10px")
      .style("pointer-events", "none");

      cricles
      .selectAll(".circle")
      .data(processed_data)
      .enter()
      .append('circle')
      .attr("class", "circle")
      .attr("cx", (d: any) => ctl.xScale(d.date))
      .attr("cy", (d: any) => ctl.yScale(d.valor))
      .attr("r", ctl.circleRadius)
      .style('opacity', ctl.circleOpacity)
      .attr('fill', 'orange')
      .on("mouseover", function (event: any, d: any) {
        // Show the tooltip and set its content
        tooltip
          .html(`<strong>Fecha:</strong> ${d3.timeFormat("%d/%m/%Y")(d.date)}<br><strong>Valor:</strong> ${d.valor} litros`)
          .style("visibility", "visible");
    
        // Highlight the circle
        d3.select(d.target)
          .transition()
          .duration(ctl.duration)
          .attr("r", ctl.circleRadiusHover)
          .style("opacity", 1);
      })
      .on("mousemove", function (event: any) {
        tooltip
          .style("top", `${event.clientY - 410}px`) // Position 10px below the mouse pointer
          .style("left", `${event.clientX - 610}px`); // Position 10px to the right of the mouse pointer
      })
      .on("mouseout", function (d:any) {
        // Hide the tooltip
        tooltip.style("visibility", "hidden");
    
        // Reset the circle style
        d3.select(d.target)
          .transition()
          .duration(ctl.duration)
          .attr("r", ctl.circleRadius)
          .style("opacity", ctl.circleOpacity);
      });

      // Add consumptions line after the other lines
      if (processed_consumptions.length > 0) {
        lines
          .datum(processed_consumptions)
          .append('path')
          .attr("d", d3.line()
            .curve(d3.curveCardinal)
            .x((d: any) => this.xScale(d.date))
            .y((d: any) => this.yScale(d.valor))
          )
          .style('fill', 'none')
          .attr("stroke", "blue")
          .attr("stroke-width", 2)
          .style('opacity', this.lineOpacity);

        // Add dots for consumptions
        cricles
          .selectAll(".circle-consumption")
          .data(processed_consumptions)
          .enter()
          .append('circle')
          .attr("class", "circle-consumption")
          .attr("cx", (d: any) => this.xScale(d.date))
          .attr("cy", (d: any) => this.yScale(d.valor))
          .attr("r", this.circleRadius)
          .style('opacity', this.circleOpacity)
          .attr('fill', 'blue')
          .on("mouseover", function (event: any, d: any) {
            tooltip
              .html(`<strong>Fecha:</strong> ${d3.timeFormat("%d/%m/%Y")(d.date)}<br><strong>Consumo:</strong> ${d.valor} litros`)
              .style("visibility", "visible");

            d3.select(event.target)
              .transition()
              .duration(ctl.duration)
              .attr("r", ctl.circleRadiusHover)
              .style("opacity", 1);
          })
          .on("mousemove", function(event: any) {
            tooltip
              .style("top", (event.pageY - 10) + "px")
              .style("left", (event.pageX + 10) + "px");
          })
          .on("mouseout", function(event: any) {
            tooltip.style("visibility", "hidden");
            
            d3.select(event.target)
              .transition()
              .duration(ctl.duration)
              .attr("r", ctl.circleRadius)
              .style("opacity", ctl.circleOpacity);
          });
      }

      // Add bar chart for consumptions
      const barWidth = 20; // Width of each bar

      this.svg.selectAll(".consumption-bar")
        .data(processed_consumptions)
        .enter()
        .append("rect")
        .attr("class", "consumption-bar")
        .attr("x", (d: any) => this.xScale(d.date) - barWidth/2)
        .attr("y", (d: any) => this.yScale(d.valor))
        .attr("width", barWidth)
        .attr("height", (d: any) => this.height - this.margin - this.yScale(d.valor))
        .attr("fill", "rgba(0, 0, 255, 0.5)")
        .on("mouseover", (event: any, d: any) => {
          const tooltip = d3.select("body").append("div")
            .attr("class", "tooltip")
            .style("position", "absolute")
            .style("background", "white")
            .style("padding", "5px")
            .style("border", "1px solid #ccc")
            .style("border-radius", "5px")
            .style("pointer-events", "none");

          const formattedValue = d.valor >= 1000 
            ? `${(d.valor/1000).toFixed(2)}K` 
            : d.valor.toFixed(2);

          tooltip
            .html(`
              <strong>Mes:</strong> ${d3.timeFormat("%B %Y")(d.date)}<br>
              <strong>Consumo Total:</strong> ${formattedValue} litros
            `)
            .style("left", (event.pageX + 10) + "px")
            .style("top", (event.pageY - 10) + "px")
            .style("opacity", 1);
        })
        .on("mouseout", () => {
          d3.selectAll(".tooltip").remove();
        });

      // Add y-axis with K formatter for large numbers
      this.svg.append("g")
        .attr("class", "y axis")
        .call(d3.axisLeft(this.yScale)
          .tickFormat((d: any) => d >= 1000 ? `${d/1000}K` : d));

      const zoom = d3.zoom()
      .scaleExtent([1, 10]) // Set the zoom scale (1x to 10x)
      .translateExtent([[0, 0], [this.width, this.height]]) // Limit panning to the chart area
      .on("zoom", (event: any) => {
        // Apply zoom transformations
        const transform = event.transform;
    
        // Update the xScale with the zoom transform
        const newXScale = transform.rescaleX(this.xScale);
    
        // Update the x-axis
        this.svg.select(".x.axis").call(d3.axisBottom(newXScale).tickFormat((domainValue: any) => d3.timeFormat("%b")(domainValue as Date)));
    
        // Update the lines and circles with the new xScale
        lines.selectAll("path")
          .attr("d", d3.line()
            .curve(d3.curveCardinal)
            .x((d: any) => newXScale(d.date))
            .y((d: any) => this.yScale(d.valor))
          );
    
        cricles.selectAll("circle, .circle-consumption")
          .attr("cx", (d: any) => newXScale(d.date))
          .attr("cy", (d: any) => this.yScale(d.valor));

        // Update consumption bars
        this.svg.selectAll(".consumption-bar")
          .attr("x", (d: any) => newXScale(d.date) - 10)
          .attr("y", (d: any) => this.yScale(d.valor))
          .attr("height", (d: any) => this.height - this.margin - this.yScale(d.valor));
      });
    
    // Apply the zoom behavior to the SVG
    this.svg
      .call(zoom)
      .on("dblclick.zoom", null);

    /* Add Axis into SVG */
    var xAxis = d3.axisBottom(this.xScale)
      .ticks(15)
      .tickFormat((domainValue: any) => d3.timeFormat("%b")(domainValue as Date));
    var yAxis = d3.axisLeft(this.yScale).ticks(10);

    this.svg.append("g")
      .attr("class", "x axis")
      .attr("transform", `translate(0, ${this.height-this.margin})`)
      .call(xAxis);

    this.svg.append("g")
      .attr("class", "y axis")
      .call(yAxis)
      .append('text')
      .attr("y", 15)
      .attr("transform", "rotate(-90)")
      .attr("fill", "#000")
      .text("Litros");

      this.width = this.chartElem.nativeElement.getBoundingClientRect().width;
      this.svg.attr('width', this.width);    
  }
}
